import { Component, Optional, OnInit, OnDestroy, inject } from '@angular/core';

import { NgxSyncDataService, NgxAssistantConfig, NgxUserServiceData } from '@odilo/ngx-assistant';
import { UserService } from '../services/user.service';
import { BroadcastService } from '../services/broadcast.service';
import { ConfigService } from '../services/config.service';
import { ClientConfiguration } from '../models/client-configuration';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HoldsFacade } from '../state/user/holds/holds.facade';
import { SearchesFacade } from '../state/user/searches/searches.facade';
import { CheckoutsFacade } from '../state/user/checkouts/checkouts.facade';
import { FavoritesFacade } from '../state/user/favorites/favorites.facade';

@Component({
  selector: 'app-assistant',
  template: `<lib-ngx-assistant-opac></lib-ngx-assistant-opac>`,
})
export class AssistantComponent implements OnInit, OnDestroy {
  private readonly clientConfiguration: ClientConfiguration = this.configService.get();
  private readonly destroy$ = new Subject<boolean>();
  private readonly favoritesFacade = inject(FavoritesFacade);
  private readonly holdsFacade = inject(HoldsFacade);
  private readonly searchesFacade = inject(SearchesFacade);
  private readonly checkoutsFacade = inject(CheckoutsFacade);

  constructor(
    private userService: UserService,
    private broadcastService: BroadcastService,
    private configService: ConfigService,
    @Optional() private syncDataService?: NgxSyncDataService
  ) {}

  ngOnInit() {
    if (this.syncDataService) {
      this.sendUserDataToAssistant();
      this.sendConfigToAssistant();
    }
  }

  private sendUserDataToAssistant() {
    this.broadcastService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => this.syncDataService.sendUserData(this.buildUserServiceData()));
  }

  private buildUserServiceData(): NgxUserServiceData {
    return {
      checkouts: this.checkoutsFacade?.checkouts,
      holds: this.holdsFacade?.holds,
      favorites: this.favoritesFacade.paginatedFavorites?.items,
      lists: this.userService.lists,
      checkoutHistory: this.userService.checkoutHistory,
      savedSearches: this.searchesFacade?.savedSearches,
      searchHistory: this.searchesFacade?.searchHistory,
      visualizationHistory: this.userService.visualizationHistory,
    };
  }

  private sendConfigToAssistant() {
    if (this.clientConfiguration) {
      this.syncDataService.sendConfig(this.buildAssistantConfig());
    }
  }

  private buildAssistantConfig(): NgxAssistantConfig {
    return {
      soundAssistant: {
        start: 'assets/sounds/alert_start.mp3',
        end: 'assets/sounds/alert_end.mp3',
        listen: 'assets/sounds/alert_listen.mp3',
        endListen: 'assets/sounds/alert_end_listen.mp3',
      },
      urlServer: this.clientConfiguration.assistant.awsUrl,
      apiKey: this.clientConfiguration.assistant.awsApiKey,
      tutorialActive: false,
    };
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
